/**
 * api.js — Axios API client helpers for the Performer app
 *
 * Two client instances:
 *   api         — unauthenticated, for public endpoints (auth, genres, instruments)
 *   authClient() — creates a fresh axios instance with the Bearer token from localStorage
 *                  (function so it always picks up the current token, not one captured at import time)
 *
 * All functions return axios Promises. Callers handle errors with .catch() or try/await.
 */

import axios from 'axios';
import { navRef } from './navRef';

function getToken() {
  return localStorage.getItem('token');
}

/** Create an axios instance pre-configured with the current auth token */
export function authClient() {
  const client = axios.create({
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  client.interceptors.response.use(
    res => res,
    err => {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        const nav = navRef.current;
        if (nav) {
          nav('/login', { state: { message: 'Your session has expired. Please sign in again.' } });
        } else {
          window.location.href = '/login';
        }
      }
      return Promise.reject(err);
    }
  );
  return client;
}

/** Unauthenticated client for public endpoints */
export const api = axios.create();

// ── Auth ──────────────────────────────────────────────────────────────────────
export const register         = (data)  => api.post('/api/auth/register', data);
export const confirmEmail     = (token) => api.get(`/api/auth/confirm-email?token=${token}`);
export const login            = (data)  => api.post('/api/auth/login', data);
export const forgotPassword   = (data)  => api.post('/api/auth/forgot-password', data);
export const validateReset    = (token) => api.get(`/api/auth/reset-password/${token}`);
export const resetPassword    = (token, data) => api.post(`/api/auth/reset-password/${token}`, data);

// ── Performer self-service ────────────────────────────────────────────────────
export const getMyProfile    = ()     => authClient().get('/api/performers/me');
export const updateMyProfile = (data) => authClient().put('/api/performers/me', data);
export const uploadPhoto      = (fd)   => authClient().post('/api/performers/me/photo', fd, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const uploadPromoPhoto = (fd)   => authClient().post('/api/performers/me/promo-photo', fd, {
  headers: { 'Content-Type': 'multipart/form-data' },
});
export const submitProfile   = ()      => authClient().post('/api/performers/me/submit');
export const changePassword  = (data)  => authClient().post('/api/auth/change-password', data);

// ── Public lookups ────────────────────────────────────────────────────────────
export const getGenres      = () => api.get('/api/performers/genres');
export const getInstruments = () => api.get('/api/performers/instruments');

// ── Admin — performer management ──────────────────────────────────────────────
export const adminGetStats           = ()           => authClient().get('/api/admin/stats');
export const adminGetPerformers      = (params)     => authClient().get('/api/admin/performers', { params });
export const adminGetPerformer       = (id)         => authClient().get(`/api/admin/performers/${id}`);
export const adminUpdateStatus       = (id, data)   => authClient().put(`/api/admin/performers/${id}/status`, data);
export const adminAddNote            = (id, data)   => authClient().post(`/api/admin/performers/${id}/notes`, data);
export const adminContactPerformer   = (id, data)   => authClient().post(`/api/admin/performers/${id}/contact`, data);
export const adminDeletePerformer    = (id)         => authClient().delete(`/api/admin/performers/${id}`);
export const adminUpdatePerformer    = (id, data)   => authClient().put(`/api/admin/performers/${id}`, data);

// ── Admin — settings ──────────────────────────────────────────────────────────
export const adminGetInstruments     = ()           => authClient().get('/api/admin/settings/instruments');
export const adminCreateInstrument   = (data)       => authClient().post('/api/admin/settings/instruments', data);
export const adminUpdateInstrument   = (id, data)   => authClient().put(`/api/admin/settings/instruments/${id}`, data);
export const adminDeleteInstrument   = (id)         => authClient().delete(`/api/admin/settings/instruments/${id}`);
export const adminGetGenres          = ()           => authClient().get('/api/admin/settings/genres');
export const adminCreateGenre        = (data)       => authClient().post('/api/admin/settings/genres', data);
export const adminUpdateGenre        = (id, data)   => authClient().put(`/api/admin/settings/genres/${id}`, data);
export const adminDeleteGenre        = (id)         => authClient().delete(`/api/admin/settings/genres/${id}`);

// ── Admin — email templates and lists ────────────────────────────────────────
export const adminGetEmailTemplates          = ()          => authClient().get('/api/admin/email-templates');
export const adminUpdateEmailTemplate        = (key, data) => authClient().put(`/api/admin/email-templates/${key}`, data);
export const adminTestEmailTemplate          = (key)       => authClient().post(`/api/admin/email-templates/${key}/test`);
export const adminGetTestRecipients          = ()          => authClient().get('/api/admin/test-email-recipients');
export const adminAddTestRecipient           = (data)      => authClient().post('/api/admin/test-email-recipients', data);
export const adminRemoveTestRecipient        = (id)        => authClient().delete(`/api/admin/test-email-recipients/${id}`);
export const adminGetAdminList               = ()          => authClient().get('/api/admin/admin-list');
export const adminGetSoundEngineeringList     = ()          => authClient().get('/api/admin/sound-engineering-list');
export const adminAddSoundEngineeringEmail    = (data)      => authClient().post('/api/admin/sound-engineering-list', data);
export const adminUpdateSoundEngineeringEmail = (id, data)  => authClient().put(`/api/admin/sound-engineering-list/${id}`, data);
export const adminRemoveSoundEngineeringEmail = (id)        => authClient().delete(`/api/admin/sound-engineering-list/${id}`);

// ── Admin — booking managers ──────────────────────────────────────────────────
export const adminGetBookingManagers    = ()         => authClient().get('/api/admin/booking-managers');
export const adminAddBookingManager     = (data)     => authClient().post('/api/admin/booking-managers', data);
export const adminUpdateBookingManager  = (id, data) => authClient().put(`/api/admin/booking-managers/${id}`, data);
export const adminRemoveBookingManager  = (id)       => authClient().delete(`/api/admin/booking-managers/${id}`);
export const adminInviteBookingManager  = (id)       => authClient().post(`/api/admin/booking-managers/${id}/invite`);

// ── Admin — venues and MCs ────────────────────────────────────────────────────
export const adminGetVenues    = ()           => authClient().get('/api/admin/venues');
export const adminCreateVenue  = (data)       => authClient().post('/api/admin/venues', data);
export const adminDeleteVenue  = (id)         => authClient().delete(`/api/admin/venues/${id}`);
export const adminGetMcs       = ()           => authClient().get('/api/admin/mcs');
export const adminCreateMc     = (data)       => authClient().post('/api/admin/mcs', data);
export const adminUpdateMc     = (id, data)   => authClient().put(`/api/admin/mcs/${id}`, data);
export const adminDeleteMc     = (id)         => authClient().delete(`/api/admin/mcs/${id}`);

// ── Admin — events ────────────────────────────────────────────────────────────
export const adminGetEvents    = (params)     => authClient().get('/api/admin/events', { params });
export const adminGetEvent     = (id)         => authClient().get(`/api/admin/events/${id}`);
export const adminCreateEvent  = (data)       => authClient().post('/api/admin/events', data);
export const adminUpdateEvent  = (id, data)   => authClient().put(`/api/admin/events/${id}`, data);
export const adminDeleteEvent  = (id)         => authClient().delete(`/api/admin/events/${id}`);
export const adminConfirmEvent   = (id)       => authClient().post(`/api/admin/events/${id}/confirm`);
export const adminUnconfirmEvent = (id)       => authClient().post(`/api/admin/events/${id}/unconfirm`);
export const adminSaveReconfirmNotes = (id, reconfirm_notes) => authClient().put(`/api/admin/events/${id}/reconfirm-notes`, { reconfirm_notes });
export const adminGetEventRecipients = (id)   => authClient().get(`/api/admin/events/${id}/recipients`);
export const adminSendTestConfirmEmail = (id) => authClient().post(`/api/admin/events/${id}/test-confirm-email`);
export const adminNotifyEventPerformer = (id, epId) => authClient().post(`/api/admin/events/${id}/performers/${epId}/notify`);

// ── Admin — running order ─────────────────────────────────────────────────────
export const adminGetPerformersList        = ()                => authClient().get('/api/admin/performers-list');
export const adminAddEventPerformer        = (eid, data)       => authClient().post(`/api/admin/events/${eid}/performers`, data);
export const adminReorderEventPerformers   = (eid, data)       => authClient().put(`/api/admin/events/${eid}/performers/reorder`, data);
export const adminUpdateEventPerformer     = (eid, epId, data) => authClient().put(`/api/admin/events/${eid}/performers/${epId}`, data);
export const adminRemoveEventPerformer     = (eid, epId)       => authClient().delete(`/api/admin/events/${eid}/performers/${epId}`);
// adminUpdateLyricSet was removed: it posted to /api/admin/events/:eid/lyric-sets/:sid,
// a route that does not exist on the server, and nothing in the UI called it.
// Lyric set titles and writers are updated through PUT /events/:id, which saves
// the event and its sets in one transaction (see routes/events.js). Wiring a
// button to the old helper would have produced a silent 404.

// ── Admin — lyricist slots ────────────────────────────────────────────────────
export const adminGetEventSlots     = (eid)            => authClient().get(`/api/admin/events/${eid}/slots`);
export const adminAddEventSlot      = (eid, data)      => authClient().post(`/api/admin/events/${eid}/slots`, data);
export const adminReorderEventSlots = (eid, data)      => authClient().put(`/api/admin/events/${eid}/slots/reorder`, data);
export const adminUpdateEventSlot   = (eid, sid, data) => authClient().put(`/api/admin/events/${eid}/slots/${sid}`, data);
export const adminRemoveEventSlot   = (eid, sid)       => authClient().delete(`/api/admin/events/${eid}/slots/${sid}`);

// ── Admin — ad hoc sets ───────────────────────────────────────────────────────
export const adminCreateAdHocSet                = (eid, data) => authClient().post(`/api/admin/events/${eid}/adhoc-sets`, data);
export const adminDeleteAdHocSet                = (eid, aid)  => authClient().delete(`/api/admin/events/${eid}/adhoc-sets/${aid}`);
export const adminUpdateAdHocSet                = (eid, aid, data) => authClient().put(`/api/admin/events/${eid}/adhoc-sets/${aid}`, data);
export const adminGetPerformerStageRequirements = (pid)       => authClient().get(`/api/admin/performers/${pid}/stage-requirements`);
export const adminGetInstrumentTypes            = ()          => authClient().get('/api/admin/instrument-types');
export const adminCreatePerformer               = (data)      => authClient().post('/api/admin/performers/create', data);
